/**
 * Helpers for `CargoDefinition.propertyGroupsJson` — the JSON document
 * describing which property groups (capacity, handling, temperature, stack,
 * bic, compartments, compliance) are ticked on a definition and what spec
 * values they carry.
 *
 * <p>
 * Wire shape mirrors the BE `CargoDefinitionPropertyGroups` C# type
 * exactly: camelCase keys, optional groups (presence = ticked), optional
 * fields inside each group (set = spec on every order; missing = prompt
 * at order entry). `bic` and `compartments` are pure markers — empty
 * object means "ticked".
 * </p>
 *
 * <p>
 * The FE works against the strong typed shape — it parses incoming
 * `propertyGroupsJson` strings from the BE into the typed object, edits
 * locally, then serialises back to JSON when sending Create/Update
 * requests. The BE handler runs its own `Canonicalize` over the value
 * before persisting (trims strings, collapses empty groups) so storage
 * stays tidy regardless of what the FE sends.
 * </p>
 */

import type { CargoPropertyGroupsPayload } from './client';

/** Every group key, in the canonical chip-row order. */
export const PROPERTY_GROUP_KEYS = [
  'capacity',
  'handling',
  'temperature',
  'stack',
  'bic',
  'compartments',
  'compliance',
] as const;

export type PropertyGroupKey = (typeof PROPERTY_GROUP_KEYS)[number];

/**
 * Parse the raw `propertyGroupsJson` string from a BE projection into the
 * strong typed shape. Returns an empty payload (no groups ticked) for
 * <c>null</c>, empty, or malformed input — the FE renders that as "no
 * chips selected" rather than throwing.
 */
export function parsePropertyGroups(raw: string | null | undefined): CargoPropertyGroupsPayload {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === 'object') {
      return parsed as CargoPropertyGroupsPayload;
    }
    return {};
  } catch {
    return {};
  }
}

/**
 * Has the operator filled in any spec values inside this group? Drives
 * the spec-vs-prompt badge on the grid + the order-line preview pane.
 *
 * <p>
 * BIC is always "prompt" regardless of payload (the chip is a marker —
 * value lives per-physical-unit at order entry). Compartments is always
 * "spec" when ticked (chip presence = the answer is yes).
 * </p>
 */
export function groupHasValues(
  group: PropertyGroupKey,
  payload: CargoPropertyGroupsPayload,
): boolean {
  if (group === 'bic') return false;
  if (group === 'compartments') return payload.compartments !== undefined;
  const g = payload[group];
  if (g === undefined) return false;
  return Object.values(g).some((v) => v !== null && v !== undefined && v !== '');
}

/** Is the chip ticked on this definition (regardless of spec/prompt mode)? */
export function isGroupActive(
  group: PropertyGroupKey,
  payload: CargoPropertyGroupsPayload,
): boolean {
  return payload[group] !== undefined;
}

/**
 * Strip an entire group from the payload (chip un-ticked). Returns a new
 * object; the caller can drop the result into form state.
 */
export function removeGroup(
  group: PropertyGroupKey,
  payload: CargoPropertyGroupsPayload,
): CargoPropertyGroupsPayload {
  const next: CargoPropertyGroupsPayload = { ...payload };
  delete next[group];
  return next;
}

/**
 * Tick a group (chip on) — sets it to the appropriate empty seed. Markers
 * (`bic`, `compartments`) become `{}`; the rest become an empty object
 * where every field is implicitly null until the operator types.
 */
export function addGroup(
  group: PropertyGroupKey,
  payload: CargoPropertyGroupsPayload,
): CargoPropertyGroupsPayload {
  return { ...payload, [group]: {} };
}

/**
 * Serialise a typed payload back to the BE wire format. Returns
 * <c>null</c> when no groups are ticked — matches the BE column's "no
 * groups" state. Otherwise returns the JSON object (the BE will run its
 * own canonicaliser over it before persisting).
 */
export function serialisePropertyGroups(payload: CargoPropertyGroupsPayload): CargoPropertyGroupsPayload | null {
  // Strip groups where every field is null / empty AND the group isn't
  // a marker — those would canonicalise to absent on the BE anyway, so
  // sending them adds noise without changing behaviour.
  const cleaned: CargoPropertyGroupsPayload = {};
  for (const key of PROPERTY_GROUP_KEYS) {
    if (!isGroupActive(key, payload)) continue;
    if (key === 'bic' || key === 'compartments') {
      // Markers — presence is the data.
      cleaned[key] = {};
      continue;
    }
    cleaned[key] = payload[key] as never;
  }
  const isEmpty = PROPERTY_GROUP_KEYS.every((k) => cleaned[k] === undefined);
  return isEmpty ? null : cleaned;
}
